import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Save, X, ArrowLeft } from 'lucide-react';
import { createInstitution } from '../services/institutionsService';
import { INSTITUTION_TYPES, INSTITUTION_SIZES } from '../services/institutionsService';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';


// Cores da marca
const BORDER = '#E5E7EB';

const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
    <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border shadow-sm ${className}`} style={{ borderColor: BORDER }}>
    {children}
  </div>
);

const CreateInstitutionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'startup',
    tagline: '',
    description: '',
    mission: '',
    website: '',
    logo_url: '',
    cover_image_url: '',
    founded_year: new Date().getFullYear(),
    country: 'Brasil',
    city: '',
    industry: '',
    size: '1-10',
    linkedin_url: '',
    twitter_url: '',
    instagram_url: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await createInstitution(formData);
      console.log('Instituição criada:', result);
      navigate('/profile'); // Redireciona para o perfil após criar
    } catch (err) {
      console.error('Erro ao criar instituição:', err);
      setError(err.response?.data?.error || 'Erro ao criar instituição');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      {/* Header */}
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              style={{ color: BRAND_GREEN }}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-extrabold" style={{ color: BRAND_GREEN }}>
                Criar Instituição
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Registre sua empresa, startup, clube ou organização na Arena Stats
              </p>
            </div>
          </div>
          
          <div className="p-3 rounded-xl border-2" style={{ borderColor: BRAND_GOLD, backgroundColor: 'white' }}>
            <Building2 className="w-6 h-6" style={{ color: BRAND_GOLD }} />
          </div>
        </div>
      </Card>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="p-6 mb-6">
          {/* Seção: Informações Básicas */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
              <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>
                Informações Básicas
              </h2>
            </div>

            <div className="space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome da Instituição *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                  placeholder="Ex: Arena Stats"
                />
              </div>

              {/* Tipo e Tamanho (Grid 2 colunas) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ borderColor: BORDER }}
                  >
                    {INSTITUTION_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tamanho
                  </label>
                  <select
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ borderColor: BORDER }}
                  >
                    {INSTITUTION_SIZES.map(size => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tagline */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tagline (resumo em uma linha)
                </label>
                <input
                  type="text"
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleChange}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                  placeholder="Ex: Dados oficiais + IA + performance. O esporte acontece — a Arena registra."
                />
                <p className="text-xs text-gray-400 mt-1">{formData.tagline.length}/500 caracteres</p>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none"
                  style={{ borderColor: BORDER }}
                  placeholder="Descreva a instituição, seus objetivos e atividades..."
                />
              </div>

              {/* Missão (Arena Mode) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Missão (Arena Mode)
                </label>
                <textarea
                  name="mission"
                  value={formData.mission}
                  onChange={handleChange}
                  rows={3}
                  maxLength={1000}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none"
                  style={{ borderColor: BORDER }}
                  placeholder="O que você está construindo? Qual o propósito?"
                />
                <p className="text-xs text-gray-400 mt-1">{formData.mission.length}/1000 caracteres</p>
              </div>
            </div>
          </div>

          {/* Seção: Localização e Indústria */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
              <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>
                Localização e Indústria
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  País
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cidade
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                  placeholder="Ex: Belo Horizonte"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ano de Fundação
                </label>
                <input
                  type="number"
                  name="founded_year"
                  value={formData.founded_year}
                  onChange={handleChange}
                  min={1900}
                  max={new Date().getFullYear()}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Indústria / Setor
              </label>
              <input
                type="text"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{ borderColor: BORDER }}
                placeholder="Ex: Sports Technology, Health & Fitness, Education"
              />
            </div>
          </div>

          {/* Seção: Links e Redes Sociais */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
              <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>
                Links e Redes Sociais
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                  placeholder="https://exemplo.com.br"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ borderColor: BORDER }}
                    placeholder="https://linkedin.com/company/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Twitter / X
                  </label>
                  <input
                    type="url"
                    name="twitter_url"
                    value={formData.twitter_url}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ borderColor: BORDER }}
                    placeholder="https://twitter.com/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Instagram
                  </label>
                  <input
                    type="url"
                    name="instagram_url"
                    value={formData.instagram_url}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ borderColor: BORDER }}
                    placeholder="https://instagram.com/..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Seção: Imagens (opcional) */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 rounded-full" style={{ backgroundColor: BRAND_GOLD }}></div>
              <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>
                Imagens (opcional)
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  name="logo_url"
                  value={formData.logo_url}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                  placeholder="https://exemplo.com/logo.png"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cover Image URL
                </label>
                <input
                  type="url"
                  name="cover_image_url"
                  value={formData.cover_image_url}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{ borderColor: BORDER }}
                  placeholder="https://exemplo.com/cover.jpg"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Error Message */}
        {error && (
          <Card className="p-4 mb-6 border-red-500">
            <div className="flex items-center gap-2 text-red-600">
              <X className="w-5 h-5" />
              <span className="font-semibold">{error}</span>
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 rounded-xl border font-semibold transition-colors hover:bg-gray-50"
            style={{ borderColor: BORDER, color: 'rgb(55,65,81)' }}
          >
            Cancelar
          </button>
          
          <button
            type="submit"
            disabled={loading || !formData.name}
            className="px-6 py-3 rounded-xl font-bold text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Criando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Criar Instituição
              </>
            )}
          </button>
        </div>
      </form>

      <div className="h-12"></div>
    </PageShell>
  );
};

export default CreateInstitutionPage;
